import { Link } from "react-router-dom";
import type { InteractionWithId } from "../../hooks/useInteractions";
import type { EntityInfo } from "../../hooks/useEntityMap";
import type { EntityType } from "../../types";
import { nextQuickActions } from "../../utils/eventOptionSelection";
import { formatEventType, formatTime } from "../../utils/tripDisplay";
import { pickStepButtons, useActivityStepOptions, useStepLogger } from "../../hooks/useStepLogger";
import { TypeIcon } from "../common/TypeIcon";

interface QuickStepRowProps {
  activity: InteractionWithId;
  entity: EntityInfo | null;
  type: EntityType | undefined;
  tripId: string;
  zone?: string;
}

/**
 * A compact row for an activity that isn't the one on the big card: the
 * airport you're still in while the Uber comes, or the one you set up from the
 * plane. Its next step is a button, logged in one tap with Undo.
 */
export function QuickStepRow({ activity, entity, type, tripId, zone }: QuickStepRowProps) {
  const options = useActivityStepOptions(activity.entityId, type);
  const { events, busy, undo, logStep, undoStep } = useStepLogger(activity.id, activity.events);
  const quick = nextQuickActions(options, events);
  const { primary, secondary } = pickStepButtons(quick.options, quick.branching);

  const name = entity?.displayName || activity.name || "Activity";
  const last = events[events.length - 1] ?? null;
  const sub = last
    ? `${last.customLabel || options.find((o) => o.eventType === last.eventType)?.displayName || formatEventType(last.eventType)} · ${formatTime(last.eventTimestamp.toDate(), last.timezone || zone)}`
    : "Not started";

  return (
    <div className="qs-row">
      <Link to={`/trips/${tripId}/interactions/${activity.id}`} className="qs-main">
        <span className="tsf-icon">
          {entity?.imageUrl ? <img src={entity.imageUrl} alt="" /> : <TypeIcon type={type} name={name} tile />}
        </span>
        <span className="tsf-text">
          <span className="tsf-name">{name}</span>
          <span className="tsf-sub">{sub}</span>
        </span>
      </Link>
      {undo ? (
        <div className="qs-actions" role="status">
          <span className="qs-logged">{undo.label}</span>
          <button type="button" className="qs-btn qs-undo" onClick={undoStep}>Undo</button>
        </div>
      ) : (
        (primary || secondary.length > 0) && (
          <div className="qs-actions">
            {primary && (
              <button type="button" className="qs-btn qs-primary" onClick={() => logStep(primary)} disabled={busy}>
                {primary.displayName}
              </button>
            )}
            {secondary.map((opt) => (
              <button key={opt.id} type="button" className="qs-btn" onClick={() => logStep(opt)} disabled={busy}>
                {opt.displayName}
              </button>
            ))}
          </div>
        )
      )}
    </div>
  );
}
